import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { api, formatApiError } from "../lib/api";

export default function WiderrufPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    try {
      await api.post("/revocations", {
        order_number: orderNumber.trim().toUpperCase(),
        email: email.trim(),
        reason: reason.trim(),
      });
      setStatus("ok");
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="widerruf-page">
      <Navigation />

      <section className="pt-28 md:pt-36 pb-24 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 text-[#D4AF37]">
            <ShieldAlert size={22} strokeWidth={1.2} />
            <span className="gold-divider">Ihr Recht</span>
          </div>

          <h1
            data-testid="widerruf-heading"
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#E5E5E5] mt-8"
          >
            Widerruf <span className="italic gold-text">einreichen</span>
          </h1>

          <p className="mt-6 text-[#E5E5E5]/75 font-sans font-light leading-relaxed">
            Sie haben das Recht, binnen{" "}
            <span className="text-[#D4AF37] font-medium">14 Tagen</span> ohne
            Angabe von Gründen diesen Vertrag zu widerrufen. Die Frist beginnt
            mit dem Tag, an dem Sie die Ware erhalten haben. Bitte geben Sie
            unten Ihre Bestellnummer und die zugehörige E-Mail-Adresse an.
          </p>

          {status === "ok" ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="mt-12 border border-[#D4AF37]/25 p-8 bg-[#0b0b0b] text-center"
              data-testid="widerruf-success"
            >
              <div className="flex justify-center text-[#D4AF37] mb-4">
                <CheckCircle2 size={44} strokeWidth={1} />
              </div>
              <h2 className="font-serif italic text-2xl text-[#E5E5E5]">
                Ihr Widerruf ist eingegangen.
              </h2>
              <p className="mt-4 text-sm text-[#A1A1AA] font-sans max-w-md mx-auto leading-relaxed">
                Nicole wurde benachrichtigt und wird sich in Kürze mit Ihnen in
                Verbindung setzen, um die Rückabwicklung zu besprechen. Bitte
                senden Sie die Ware noch nicht zurück, bevor Sie eine Bestätigung
                erhalten haben.
              </p>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-12 space-y-6 border border-[#D4AF37]/15 p-6 md:p-8 bg-[#0b0b0b]"
              data-testid="widerruf-form"
            >
              <Field label="Bestellnummer">
                <input
                  required
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="LM-XXXXXX"
                  data-testid="widerruf-order-number"
                  className="w-full bg-transparent border border-[#D4AF37]/20 px-3 py-3 font-serif text-lg tracking-wider text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60 uppercase"
                />
              </Field>

              <Field label="E-Mail (wie bei Bestellung angegeben)">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="widerruf-email"
                  className="w-full bg-transparent border border-[#D4AF37]/20 px-3 py-3 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60"
                />
              </Field>

              <Field label="Begründung (optional)">
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  data-testid="widerruf-reason"
                  className="w-full bg-transparent border border-[#D4AF37]/20 px-3 py-3 font-sans text-sm text-[#E5E5E5] focus:outline-none focus:border-[#D4AF37]/60 resize-none"
                />
              </Field>

              {error && (
                <p className="text-sm text-red-400 font-sans" data-testid="widerruf-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="widerruf-submit"
                className="btn-gold w-full px-8 py-4 font-sans font-light tracking-widest text-xs uppercase disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {status === "sending" ? "Wird gesendet…" : "Widerruf absenden"}
              </button>
            </form>
          )}

          <div className="mt-16 border-t border-[#D4AF37]/10 pt-8">
            <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]/70 font-sans mb-4">
              Musterformular gemäß § 355 BGB
            </p>
            <p className="text-sm text-[#E5E5E5]/70 font-sans font-light leading-relaxed">
              „Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über
              den Kauf der folgenden Waren. Bestellt am: __________, erhalten
              am: __________. Name des Verbrauchers, Anschrift, Datum,
              Unterschrift (nur bei Papierform).“
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.35em] text-[#A1A1AA] font-sans">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);
